/* eslint-disable max-statements, max-lines, complexity, camelcase */
import React, {Component, type Node} from 'react';
import {connect} from 'react-redux';
import Store from 'store';
import {Helmet} from 'react-helmet';
import classNames from 'classnames';
import {withRouter} from 'react-router-dom';
import {SnackBarsContainer, Theme} from '@optimaxdev/design-system';
import {MarketingAPI} from '@optimaxdev/marketing';
import {withProfiler} from '@sentry/react';

import type {DispatchType} from 'constants/flow/flowTypes';

import {getQuoteItems} from 'actions/checkout/checkout';
import {Router} from 'router/router';
import {Feature} from 'components/feature/feature';
import {RELEASE_VERSION} from 'constants/app';
import {CUSTOM_DATA} from 'constants/analytics/speedCurve';
import {PrivacyPolicyBanner} from 'features/experiments/privacyPolicyBanner/privacyPolicyBanner';
import {PrivacyPolicyWrapper} from 'features/experiments/privacyPolicyBanner/privacyPolicyWrapper';
import {getCanonicalUrl} from 'libs/url';
import {sendCustomDimensionEvent} from 'libs/analytics/setDimension';
import {addCustomData} from 'libs/analytics/sendAnalytics';
import {instanceGusaBackend} from 'libs/api';
import {TryOnWrapperConnected} from 'containers/tryOnWrapper/tryOnWrapper';
import {DialogConnected} from 'components/dialog/dialog';
import withCookies from 'libs/cookie/withCookies';
import {paymentMethodCode} from 'selectors/checkout/checkout';
import {HeaderStickyElementsConnected} from 'components/quickSearchSticky/quickSearchSticky';

import s from './app.scss';

type PropsType = {
    dispatch: DispatchType,
    location: {pathname: string, search: string},
    page: string,
};

/**
 * Class for superApp
 */
export class App extends Component<PropsType> {
    /**
     * Called after component mounted
     */
    componentDidMount() {
        const {dispatch} = this.props;

        addCustomData(CUSTOM_DATA.VERSION, RELEASE_VERSION || '');

        dispatch(getQuoteItems());

        const el = document.getElementById('home-loader'); // TODO Mary we do not have 'home-loader' element
        if (el && el.parentNode) el.parentNode.removeChild(el);

        setTimeout(() => {
            Store.remove('joinClub');
        }, 8000);

        const marketing = new MarketingAPI({
            headers: instanceGusaBackend.defaults.headers,
        });

        window.marketingAPI = {
            subscribe: marketing.subscribe,
            setCoupon: marketing.setCoupon,
            setQuote: marketing.setQuote,
            getQuote: marketing.getQuote,
            setPhoneNumber: marketing.setPhoneNumber,
        };

        window.addEventListener('beforeunload', sendCustomDimensionEvent);
    }

    /**
     * Called when component will be unmounted
     */
    componentWillUnmount() {
        window.removeEventListener('beforeunload', sendCustomDimensionEvent);
    }

    // todo Mary move all logic to certain pages one by one
    /**
     * Render jsx to html
     *
     * @returns {Node} Rendered react component
     */
    render(): Node {
        const {page, location} = this.props;
        const subscribe = Store.get('joinClub') || '';

        Store.remove('joinClub');

        const isPairfectMatchPage = location.pathname.startsWith('/pairfect_match');
        const isWishlist = location.pathname.startsWith('/wishlist');

        return (
            <Theme theme={{isMobile: true}}>
                <Helmet>
                    <link rel="canonical" href={getCanonicalUrl(location.pathname)} />
                </Helmet>
                <HeaderStickyElementsConnected page={page} />

                {subscribe ? <p className={s.subscribe}>Thank you for your subscription.</p> : null}
                {/*
                    content class for prevent footer jumping if
                    page not defined (it is on ~0.1 sec when page load)
                */}
                <div
                    className={classNames({
                        [s.content]: Boolean(!page) && !isWishlist,
                        [s.pairfectMatch]: isPairfectMatchPage,
                        [s.wishlist]: isWishlist,
                    })}
                >
                    <Router location={location} />
                </div>
                <Feature flag="expPrivacyPolicyM" fallback={null}>
                    <PrivacyPolicyWrapper>
                        <PrivacyPolicyBanner />
                    </PrivacyPolicyWrapper>
                </Feature>
                <DialogConnected />
                <TryOnWrapperConnected />
                {!__NODEJS__ && <SnackBarsContainer />}
            </Theme>
        );
    }
}

export const AppWithoutHot = withRouter(
    withProfiler(
        connect(store => ({
            page: store.pageApp.page,
            router: store.router,
            params: store.pageApp.params,
            paymentCode: paymentMethodCode(store),
        }))(withCookies(App)),
        {name: 'AppWithoutHot'},
    ),
);

export default AppWithoutHot;
